import { useEffect, useState } from "react";
import clsx from "clsx";
import type { AutocompleteOption } from "@/components/Autocomplete";
import { Dialog } from "@/components/ui/Dialog";

/** Nur http(s)-Adressen aus dem Lieferantenkatalog anzeigen. */
export function artikelImageSrc(value: string | null | undefined): string | null {
  const text = String(value || "").trim();
  if (!text) return null;
  const normalized = text.startsWith("//") ? `https:${text}` : text;
  if (normalized.startsWith("https://") || normalized.startsWith("http://")) return normalized;
  return null;
}

const SIZE_CLASS = {
  sm: "h-10 w-10",
  md: "h-16 w-16",
  lg: "h-36 w-36",
} as const;

export function ArtikelImage({
  src,
  alt = "",
  size = "sm",
  expandable = false,
  className,
}: {
  src?: string | null;
  alt?: string;
  size?: keyof typeof SIZE_CLASS;
  /** Klick öffnet das Bild größer. In Auswahllisten weglassen, damit die Zeile gewählt wird. */
  expandable?: boolean;
  className?: string;
}) {
  const safe = artikelImageSrc(src);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setFailed(false);
    setOpen(false);
  }, [safe]);

  if (!safe || failed) return null;

  const image = (
    <img
      src={safe}
      alt={alt}
      loading="lazy"
      className={clsx(
        "shrink-0 rounded-md bg-white object-contain ring-1 ring-inset ring-black/10",
        SIZE_CLASS[size],
        className,
      )}
      onError={() => setFailed(true)}
    />
  );

  if (!expandable) return image;

  return (
    <>
      <button
        type="button"
        className="shrink-0 rounded-md"
        title="Lieferantenbild vergrößern"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        {image}
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title={alt || "Artikelbild"} size="lg">
        <img src={safe} alt={alt} className="mx-auto max-h-[70vh] w-full object-contain" />
      </Dialog>
    </>
  );
}

export function withArtikelImage<T extends { image_src?: string | null; label?: string | null }>(
  option: AutocompleteOption<T>,
): AutocompleteOption<T> {
  const src = artikelImageSrc(option.value?.image_src);
  if (!src) return option;
  return {
    ...option,
    leading: (
      <ArtikelImage src={src} alt={option.label || ""} size="sm" />
    ),
  };
}
